import { motion } from 'framer-motion';
import { BookOpen, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeo } from '../hooks/useSeo';

interface GlossaryTerm {
  term: string;
  definition: string;
  context?: string;
}

const glossaryTerms: GlossaryTerm[] = [
  {
    term: "AI Voice Caller",
    definition: "An AI voice caller is an automated software system that places and receives phone calls using a synthetic voice powered by artificial intelligence. Unlike a simple robocall that plays a pre-recorded message, an AI voice caller can listen to responses, understand natural language, and hold a full two-way conversation to qualify leads, answer objections, and book appointments.",
    context: "Point Break Systems builds custom AI voice callers for real estate professionals that integrate directly with their CRM and lead sources."
  },
  {
    term: "Speed to Lead",
    definition: "Speed to lead is the elapsed time between a prospect submitting their contact information and receiving a follow-up call. Research from the Harvard Business Review and MIT found that contacting a lead within 5 minutes makes you 100 times more likely to qualify them compared to calling 30 minutes later. After 24 hours, the lead contact rate drops by over 80%.",
    context: "AI ISA replacement systems from Point Break Systems achieve a speed to lead of 30 seconds, operating 24 hours a day, 7 days a week."
  },
  {
    term: "CASL (Canadian Anti-Spam Legislation)",
    definition: "CASL is Canada's primary anti-spam and electronic communications law, which also governs commercial telephone calls to consumers. It requires that businesses have either express or implied consent before contacting a person for commercial purposes. CASL mandates clear identification of the caller, an easy opt-out mechanism, and record keeping of consent.",
    context: "All Point Break Systems AI callers deployed in Canada are configured for CASL compliance including consent logging and automatic opt-out handling."
  },
  {
    term: "TCPA (Telephone Consumer Protection Act)",
    definition: "The TCPA is the primary US federal law governing telemarketing calls, auto-dialed calls, prerecorded messages, and text messages to consumers. It restricts calling hours (generally 8am to 9pm local time), requires adherence to the National Do Not Call Registry, and mandates disclosure for automated calling systems.",
    context: "AI callers deployed by Point Break Systems for US-based real estate teams are configured to meet all TCPA requirements."
  },
  {
    term: "ISA (Inside Sales Agent)",
    definition: "An ISA is a dedicated employee whose primary responsibility is prospecting, making outbound calls, following up with leads, and booking appointments for real estate agents or teams. An ISA typically earns $40,000 to $70,000 per year, works standard business hours, and can handle one call at a time.",
    context: "An AI ISA replacement from Point Break Systems performs the same core function as an ISA at a fraction of the cost, calling every lead in 30 seconds, available 24/7, handling unlimited simultaneous calls."
  },
  {
    term: "Lead Qualification",
    definition: "Lead qualification is the process of determining whether a prospect has genuine interest, the financial capacity, and the realistic timeline to transact. In real estate, this typically means confirming motivation (why they want to buy or sell), timeline (when they need to move), and financial readiness (pre-approval or equity). Qualification separates prospects worth pursuing from those who are not ready.",
    context: "AI callers can run through a full qualification script on every inbound lead in real time, logging results directly to the CRM."
  },
  {
    term: "CRM Integration",
    definition: "CRM (Customer Relationship Management) integration is the connection between an AI caller and the database where lead and contact information is stored. A proper integration means new leads are automatically called when they enter the CRM, call outcomes are written back to the contact record, and next actions are scheduled, without any manual data entry.",
    context: "Point Break Systems integrates with Follow Up Boss, kvCORE, HubSpot, Salesforce, LionDesk, Chime, and most other real estate CRM platforms."
  },
  {
    term: "Cold Calling",
    definition: "Cold calling is the practice of contacting a prospective customer who has had no prior relationship with the caller. In real estate, cold calling typically targets homeowners in a geographic farm area, expired listings, or for-sale-by-owner (FSBO) properties. The goal is to introduce the agent, gauge interest in listing or buying, and book an appointment.",
    context: "AI callers are particularly effective for high-volume cold calling because they never experience call reluctance, maintain consistent energy, and can make hundreds of calls per day."
  },
  {
    term: "Lead Reactivation",
    definition: "Lead reactivation is the process of re-engaging contacts in a database who previously expressed interest but never converted. These are often leads who were not ready to transact 6 to 24 months ago and may now be closer to a decision. Reactivation campaigns are among the highest-ROI activities in real estate because the leads are already warm.",
    context: "AI callers can systematically work through a dormant database of hundreds or thousands of contacts to identify who is ready to transact now."
  },
  {
    term: "Appointment Setting",
    definition: "Appointment setting is the act of scheduling a meeting (such as a buyer consultation, listing presentation, or property showing) between a real estate agent and a qualified prospect. In the context of AI calling, appointment setting means the AI identifies qualified leads, confirms their interest and availability, and books a time on the agent's calendar automatically.",
    context: "The primary outcome metric for Point Break Systems clients is qualified appointments booked per month."
  },
  {
    term: "Natural Language Processing (NLP)",
    definition: "Natural language processing is the branch of artificial intelligence that enables computers to understand, interpret, and generate human language. In the context of AI voice callers, NLP allows the system to understand what a prospect says (including informal phrasing, accents, and incomplete sentences) and respond appropriately in real time.",
    context: "Modern NLP models power the conversational ability of AI callers, enabling them to handle objections, answer questions, and guide prospects through a qualification conversation."
  },
  {
    term: "Conversational AI",
    definition: "Conversational AI refers to AI systems designed to simulate human conversation across voice or text interfaces. Unlike simple menu-driven phone trees (IVR systems), conversational AI handles open-ended questions and unexpected responses by understanding context and intent. A conversational AI caller can hold an unscripted dialogue within defined parameters.",
    context: "Point Break Systems deploys conversational AI trained on real estate sales conversations, making interactions feel natural rather than robotic."
  },
  {
    term: "Voice Cloning",
    definition: "Voice cloning is a technology that creates a synthetic replica of a specific person's voice using a short audio sample. In AI calling, voice cloning allows a custom caller persona to be built using a real or designed voice rather than a generic text-to-speech voice, resulting in a more natural-sounding and trustworthy caller experience.",
    context: "Point Break Systems can configure callers with cloned voices for agents who want the AI to sound like a branded team member."
  },
  {
    term: "Dialer",
    definition: "A dialer is software that automates the process of placing telephone calls. Types include predictive dialers (which call multiple numbers simultaneously and connect answered calls to available agents), power dialers (which call one number at a time in sequence), and AI dialers (which pair automated dialing with conversational AI to eliminate the need for a human agent entirely).",
    context: "An AI voice caller is distinct from a traditional dialer in that it does not need a human agent to take over the call. It handles the full conversation autonomously."
  },
  {
    term: "Bilingual AI Caller",
    definition: "A bilingual AI caller is configured to conduct conversations in two languages, typically detecting the caller's preferred language from their first response and switching automatically. For Canadian real estate markets, bilingual English and French capability is essential for serving Quebec and Francophone communities across the country.",
    context: "Point Break Systems offers bilingual English and French AI callers as a standard configuration for Canadian real estate deployments."
  },
];

export default function GlossaryPage() {
  useSeo({
    title: 'AI Calling Glossary | Key Terms Explained | Point Break Systems',
    description: 'Plain-English definitions of AI calling terms for realtors: ISA replacement, speed to lead, CASL, TCPA, conversational AI, lead qualification, CRM integration, and more.',
    path: '/glossary',
    schemas: [
      ...glossaryTerms.map(t => ({
        "@context": "https://schema.org",
        "@type": "DefinedTerm",
        "name": t.term,
        "description": t.definition,
        "inDefinedTermSet": {
          "@type": "DefinedTermSet",
          "name": "Point Break Systems AI Calling Glossary",
          "url": "https://pointbreak.systems/glossary"
        }
      })),
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://pointbreak.systems" },
          { "@type": "ListItem", "position": 2, "name": "Glossary", "item": "https://pointbreak.systems/glossary" }
        ]
      },
    ],
  });

  const alphabet = [...new Set(glossaryTerms.map(t => t.term[0]))].sort();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 md:py-20">

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6 sm:mb-14"
        >
          <div className="inline-flex items-center gap-2 bg-[#d4a10d]/10 border border-[#d4a10d]/20 rounded-full px-4 py-2 text-sm font-semibold text-[#b8940b] mb-6">
            <BookOpen className="w-4 h-4" />
            Glossary
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-5 leading-tight">
            AI calling terms,<br className="hidden md:block" /> explained plainly
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            No jargon. These are the terms that come up most when discussing AI voice callers, lead automation, and compliance for real estate professionals.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2 justify-center mb-6 sm:mb-12">
          {alphabet.map(letter => (
            <a
              key={letter}
              href={`#${letter}`}
              className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold bg-white border-2 border-gray-200 text-gray-600 hover:border-[#d4a10d]/50 hover:text-[#d4a10d] transition-colors duration-200"
            >
              {letter}
            </a>
          ))}
        </div>

        <div className="space-y-4 sm:space-y-6">
          {glossaryTerms.map((item, index) => (
            <motion.div
              key={item.term}
              id={item.term[0]}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.03 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg border border-gray-200 p-4 sm:p-7 hover:border-[#d4a10d]/30 hover:shadow-md transition-all duration-300"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-3">{item.term}</h2>
              <p className="text-gray-700 leading-relaxed mb-4 text-base">{item.definition}</p>
              {item.context && (
                <div className="border-l-4 border-[#d4a10d] pl-4 mt-4">
                  <p className="text-sm text-gray-600 leading-relaxed italic">{item.context}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 bg-gray-900 rounded-lg p-5 sm:p-10 text-center text-white"
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to put these into practice?</h2>
          <p className="text-gray-300 mb-8 text-base leading-relaxed max-w-xl mx-auto">
            Book a free strategy call to see a live AI caller demo and discuss which setup fits your real estate business.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#d4a10d] hover:bg-[#b8940b] text-white font-bold px-8 py-4 rounded-full transition-colors duration-200 text-base"
          >
            Book a Strategy Call
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}

